import { useMemo } from 'react'

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'

import { type ProductFormSchema } from '~/products/hooks/useProductForm'

type ChannelProduct = ProductFormSchema['channel_products'][0]

type Props = {
    channelProducts: ChannelProduct[]
    logisticsCol: Pick<ColumnDef<ChannelProduct>, 'cell'>
    actionsCol: Pick<ColumnDef<ChannelProduct>, 'cell'>
}

export function LogisticsTable({ channelProducts, actionsCol, logisticsCol }: Props) {
    const uniqueChannelProducts = useMemo(() => {
        const existingShops = new Set<number>()

        return channelProducts.filter((cp) => {
            if (existingShops.has(cp.shop.id)) {
                return false
            }

            existingShops.add(cp.shop.id)
            return true
        })
    }, [channelProducts])

    const table = useReactTable({
        data: uniqueChannelProducts,
        columns: [
            {
                accessorFn: ({ shop }) => shop.shop_name,
                header: gettext('Shop'),
                cell: ({ getValue }) => getValue(),
            },
            {
                id: 'enabled-logistics',
                header: gettext('Enabled logistics'),
                ...logisticsCol,
            },
            {
                id: 'actions',
                header: gettext('Actions'),
                ...actionsCol,
            },
        ],
        getCoreRowModel: getCoreRowModel(),
    })

    return (
        <TableContainer sx={{ mt: 1, mb: 2 }}>
            <Table size="small">
                <TableHead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableCell
                                    key={header.id}
                                    align={header.id.includes('actions') ? 'center' : 'inherit'}
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <TableCell
                                    key={cell.id}
                                    align={cell.id.includes('actions') ? 'center' : 'inherit'}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
